/* The whiteboard — an infinite canvas.

   Same vector-stroke model as the notebook, without the page boundaries.

   Moved out of app.html unchanged apart from the onReady() wrapper. That
   wrapper is not cosmetic: this used to open with `if (!wrap) return;`, and
   running from the bundle in <head> — before the markup exists — that guard
   would fire on every load and the whole editor would silently not be there.
*/

import { onReady } from '../lib/ready.js';

export function initWhiteboard() {
  const $ = id => document.getElementById(id);
  const wrap = $('wb-wrap');
  if (!wrap) return;   // now only true if the markup is genuinely absent

  const PALETTE = ['#111827', '#6b7280', '#dc2626', '#ea580c', '#ca8a04', '#16a34a',
                   '#0891b2', '#2563eb', '#4f46e5', '#9333ea', '#db2777', '#ffffff'];
  const TOOL_STYLE = {
    pen:         { alpha: 1,    scale: 1,   composite: 'source-over' },
    pencil:      { alpha: 0.78, scale: 0.9, composite: 'source-over' },
    highlighter: { alpha: 0.32, scale: 4.5, composite: 'multiply' }
  };

  let wb = null;         // { note, strokes, texts, images, dirty }
  let view = { x: 0, y: 0, s: 1 };   // world point at the stage's top-left, plus zoom
  let tool = 'pen', toolType = 'pen', color = '#111827', width = 1.6;
  let eraserMode = 'standard', eraserR = 8;
  const redoStack = [];
  let editing = null, saveTimer = null;
  const imgCache = new Map();

  const stage = $('wb-stage');
  const canvas = $('wb-canvas');

  /* Screen ⇄ world. Everything else in here is written in world units, so a
     stroke drawn at 40% zoom lands where it was drawn when you zoom back in. */
  const toWorld = e => {
    const r = stage.getBoundingClientRect();
    return [view.x + (e.clientX - r.left) / view.s, view.y + (e.clientY - r.top) / view.s];
  };
  const sx = wx => (wx - view.x) * view.s;
  const sy = wy => (wy - view.y) * view.s;

  function markDirty() {
    if (!wb) return;
    wb.dirty = true;
    $('wb-saved').textContent = 'Saving…';
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(save, 900);
  }

  function drawStroke(ctx, s) {
    const st = TOOL_STYLE[s.tool] || TOOL_STYLE.pen;
    const pts = s.points || [];
    if (pts.length < 2) return;
    const base = Math.max(0.4, s.width * st.scale);
    ctx.globalAlpha = st.alpha;
    ctx.globalCompositeOperation = st.composite;
    ctx.strokeStyle = s.color;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    /* Straight segments between samples is what made this look faceted — at
       speed the pointer only reports every few pixels, so the "line" was
       literally a polygon. Each span is now a quadratic curve bending through
       the sample point, with the control points at the midpoints of its
       neighbours. Width still varies per span, which is why this cannot be one
       long path. */
    const mid = (a, b) => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
    for (let i = 1; i < pts.length; i++) {
      const p0 = pts[i - 1], p1 = pts[i];
      const a = (i === 1) ? p0 : mid(pts[i - 2], p0);
      const b = mid(p0, p1);
      const pr = ((p0[2] == null ? 0.5 : p0[2]) + (p1[2] == null ? 0.5 : p1[2])) / 2;
      ctx.lineWidth = s.tool === 'highlighter' ? base : base * (0.45 + pr * 1.25);
      ctx.beginPath();
      ctx.moveTo(a[0], a[1]);
      ctx.quadraticCurveTo(p0[0], p0[1], b[0], b[1]);
      ctx.stroke();
    }
    // The curve above stops at the last midpoint; close the gap to the tip.
    const n = pts.length;
    if (n >= 2) {
      const last = pts[n - 1], prev = pts[n - 2];
      const b = mid(prev, last);
      ctx.beginPath();
      ctx.moveTo(b[0], b[1]);
      ctx.lineTo(last[0], last[1]);
      ctx.stroke();
    }
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
  }

  /* Pointer events fire faster than the screen refreshes, so drawing on every
     one of them does work nobody ever sees. One paint per frame. */
  let rafPending = false;
  function draw() {
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame(() => { rafPending = false; paint(); });
  }

  function paint() {
    if (!wb) return;
    const r = stage.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    if (canvas.width !== Math.round(r.width * dpr)) {
      canvas.width = Math.round(r.width * dpr);
      canvas.height = Math.round(r.height * dpr);
    }
    const ctx = canvas.getContext('2d');
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // One transform for the whole scene: scale by zoom, shift by the view.
    ctx.setTransform(dpr * view.s, 0, 0, dpr * view.s, -view.x * view.s * dpr, -view.y * view.s * dpr);
    /* The grid used to be a nested loop of fillRect calls in world space. At
       100% that is a couple of thousand rectangles a frame, which is fine; at
       20% zoom the visible world quadruples in each direction and it becomes
       nearly fifty thousand — which is exactly why panning felt like wading.
       It is a CSS background on the stage now: the compositor draws it, and it
       costs the same at any zoom. */
    const step = 24 * view.s;
    stage.style.backgroundSize = step + 'px ' + step + 'px';
    stage.style.backgroundPosition = (-view.x * view.s) + 'px ' + (-view.y * view.s) + 'px';
    (wb.strokes || []).forEach(s => drawStroke(ctx, s));
    positionOverlays();
    $('wb-zoom').textContent = Math.round(view.s * 100) + '%';
  }

  /* Text boxes and images are DOM, so they stay editable and selectable — but
     they have to be repositioned from the same transform on every pan and
     zoom, or they slide away from the ink. */
  function positionOverlays() {
    $('wb-layer').querySelectorAll('.nb-txtbox').forEach(box => {
      const t = wb.texts[+box.dataset.ti];
      if (!t) return;
      box.style.left = sx(t.x) + 'px';
      box.style.top = sy(t.y) + 'px';
      box.style.width = (t.w * view.s) + 'px';
      const ed = box.querySelector('.nb-txt');
      if (ed) ed.style.fontSize = ((t.size || 24) * view.s) + 'px';
    });
    $('wb-layer').querySelectorAll('.wb-img').forEach(el => {
      const im = wb.images[+el.dataset.ii];
      if (!im) return;
      el.style.left = sx(im.x) + 'px';
      el.style.top = sy(im.y) + 'px';
      el.style.width = (im.w * view.s) + 'px';
      el.style.height = (im.h * view.s) + 'px';
    });
  }

  function renderOverlays() {
    const host = $('wb-layer');
    host.innerHTML = '';
    (wb.texts || []).forEach((t, ti) => host.appendChild(buildText(t, ti)));
    (wb.images || []).forEach((im, ii) => host.appendChild(buildImage(im, ii)));
    positionOverlays();
  }

  function buildText(t, ti) {
    const box = document.createElement('div');
    box.className = 'nb-txtbox';
    box.dataset.ti = ti;
    const el = document.createElement('div');
    el.className = 'nb-txt';
    el.contentEditable = 'true';
    el.style.fontWeight = t.bold ? '700' : '400';
    el.style.fontStyle = t.italic ? 'italic' : 'normal';
    el.style.color = t.color || '#111827';
    el.textContent = t.text || '';
    el.addEventListener('focus', () => openBar(box, el, t));
    el.addEventListener('input', () => { t.text = el.innerText; markDirty(); });
    el.addEventListener('blur', () => setTimeout(() => {
      const ae = document.activeElement;
      if (ae === el || (ae && ae.closest && ae.closest('#wb-textbar'))) return;
      closeBar(el);
    }, 150));

    const grip = document.createElement('span');
    grip.className = 'nb-txt-grip';
    grip.innerHTML = '&#10021;';
    let d = null;
    grip.addEventListener('pointerdown', ev => {
      ev.preventDefault(); ev.stopPropagation();
      grip.setPointerCapture(ev.pointerId);
      const [wx, wy] = toWorld(ev);
      d = { dx: wx - t.x, dy: wy - t.y };
    });
    grip.addEventListener('pointermove', ev => {
      if (!d) return;
      const [wx, wy] = toWorld(ev);
      t.x = wx - d.dx; t.y = wy - d.dy;
      positionOverlays();
    });
    const end = () => { if (d) { d = null; markDirty(); } };
    grip.addEventListener('pointerup', end);
    grip.addEventListener('pointercancel', end);

    const rz = document.createElement('span');
    rz.className = 'nb-txt-rz';
    let rd = null;
    rz.addEventListener('pointerdown', ev => {
      ev.preventDefault(); ev.stopPropagation();
      rz.setPointerCapture(ev.pointerId);
      rd = true;
    });
    rz.addEventListener('pointermove', ev => {
      if (!rd) return;
      const [wx] = toWorld(ev);
      t.w = Math.max(60, wx - t.x);
      positionOverlays();
    });
    const endRz = () => { if (rd) { rd = null; markDirty(); } };
    rz.addEventListener('pointerup', endRz);
    rz.addEventListener('pointercancel', endRz);

    box.appendChild(el); box.appendChild(grip); box.appendChild(rz);
    return box;
  }

  function buildImage(im, ii) {
    const el = document.createElement('div');
    el.className = 'wb-img';
    el.dataset.ii = ii;
    const img = document.createElement('img');
    img.src = im.url;
    img.draggable = false;
    el.appendChild(img);

    let d = null;
    img.addEventListener('pointerdown', ev => {
      if (tool !== 'pen' && tool !== 'text') return;   // erasing should not drag
      ev.preventDefault(); ev.stopPropagation();
      img.setPointerCapture(ev.pointerId);
      const [wx, wy] = toWorld(ev);
      d = { dx: wx - im.x, dy: wy - im.y };
    });
    img.addEventListener('pointermove', ev => {
      if (!d) return;
      const [wx, wy] = toWorld(ev);
      im.x = wx - d.dx; im.y = wy - d.dy;
      positionOverlays();
    });
    const end = () => { if (d) { d = null; markDirty(); } };
    img.addEventListener('pointerup', end);
    img.addEventListener('pointercancel', end);

    const rz = document.createElement('span');
    rz.className = 'wb-img-rz';
    let rd = null;
    rz.addEventListener('pointerdown', ev => {
      ev.preventDefault(); ev.stopPropagation();
      rz.setPointerCapture(ev.pointerId); rd = im.w / im.h;
    });
    rz.addEventListener('pointermove', ev => {
      if (!rd) return;
      const [wx] = toWorld(ev);
      im.w = Math.max(40, wx - im.x);
      im.h = im.w / rd;                 // keep the aspect; squashed photos look broken
      positionOverlays();
    });
    const endRz = () => { if (rd) { rd = null; markDirty(); } };
    rz.addEventListener('pointerup', endRz);
    rz.addEventListener('pointercancel', endRz);

    const del = document.createElement('button');
    del.className = 'wb-img-del';
    del.type = 'button';
    del.innerHTML = '&times;';
    del.addEventListener('click', ev => {
      ev.stopPropagation();
      wb.images.splice(ii, 1);
      renderOverlays();
      markDirty();
    });

    el.appendChild(rz); el.appendChild(del);
    return el;
  }

  // ---- text bar ----
  function openBar(box, el, t) {
    editing = { box, el, t };
    const bar = $('wb-textbar');
    const r = el.getBoundingClientRect();
    bar.hidden = false;
    bar.style.left = Math.max(10, r.left) + 'px';
    bar.style.top = Math.max(10, r.top - 48) + 'px';
    $('wb-tb-size').value = String(t.size || 24);
  }
  function closeBar(el) {
    if (editing && (!el || editing.el === el)) { $('wb-textbar').hidden = true; editing = null; }
  }

  // ---- erasing ----
  /* Distance from a point to a line SEGMENT. Testing only the sample points
     was the bug: at speed those can be twenty pixels apart, so a small eraser
     passed straight between two of them and hit nothing — the line looked
     untouched even though you had drawn right across it. */
  function segDist(px, py, ax, ay, bx, by) {
    const dx = bx - ax, dy = by - ay;
    const l2 = dx * dx + dy * dy;
    if (!l2) return Math.hypot(px - ax, py - ay);
    let t = ((px - ax) * dx + (py - ay) * dy) / l2;
    t = t < 0 ? 0 : t > 1 ? 1 : t;
    return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
  }

  function eraseAt(wx, wy) {
    const r = eraserR;
    // A point counts as erased if it is inside the eraser, or if the span
    // arriving at it passes through the eraser.
    const hitAt = (pts, i) => {
      const p = pts[i];
      if (Math.hypot(p[0] - wx, p[1] - wy) <= r) return true;
      if (i > 0 && segDist(wx, wy, pts[i - 1][0], pts[i - 1][1], p[0], p[1]) <= r) return true;
      if (i < pts.length - 1 && segDist(wx, wy, p[0], p[1], pts[i + 1][0], pts[i + 1][1]) <= r) return true;
      return false;
    };
    const hit = p => Math.hypot(p[0] - wx, p[1] - wy) <= r;
    if (eraserMode === 'stroke') {
      const before = wb.strokes.length;
      wb.strokes = wb.strokes.filter(s => !(s.points || []).some((p, i) => hitAt(s.points, i)));
      if (wb.strokes.length !== before) { draw(); markDirty(); }
      return;
    }
    let changed = false;
    const out = [];
    wb.strokes.forEach(s => {
      const pts = s.points || [];
      if (!pts.some((p, i) => hitAt(pts, i))) { out.push(s); return; }
      changed = true;
      let run = [];
      pts.forEach((p, i) => {
        if (hitAt(pts, i)) { if (run.length > 1) out.push({ ...s, points: run }); run = []; }
        else run.push(p);
      });
      if (run.length > 1) out.push({ ...s, points: run });
    });
    if (changed) { wb.strokes = out; draw(); markDirty(); }
  }

  // ---- pointer on the stage ----
  let drawing = null, panning = null;
  canvas.addEventListener('pointerdown', e => {
    if (!wb) return;
    const [wx, wy] = toWorld(e);

    /* Middle mouse, or a finger while the pen is the active tool, pans. On a
       tablet that is what a hand resting and dragging means; on a desktop the
       wheel does it. */
    if (e.button === 1 || e.pointerType === 'touch') {
      canvas.setPointerCapture(e.pointerId);
      panning = { px: e.clientX, py: e.clientY };
      return;
    }

    if (tool === 'text') {
      wb.texts.push({ x: wx, y: wy, w: 260, text: '', size: 24, bold: false, italic: false });
      markDirty();
      renderOverlays();
      const box = $('wb-layer').querySelector(`.nb-txtbox[data-ti="${wb.texts.length - 1}"]`);
      const ed = box && box.querySelector('.nb-txt');
      if (ed) { ed.focus(); openBar(box, ed, wb.texts[wb.texts.length - 1]); }
      return;
    }
    if (tool === 'eraser') {
      canvas.setPointerCapture(e.pointerId);
      drawing = { erase: true };
      eraseAt(wx, wy);
      return;
    }
    canvas.setPointerCapture(e.pointerId);
    redoStack.length = 0;
    const pr = (e.pointerType === 'pen' && e.pressure > 0) ? e.pressure : 0.5;
    drawing = { tool: toolType, color, width, points: [[wx, wy, +pr.toFixed(2)]] };
    wb.strokes.push(drawing);
  });

  canvas.addEventListener('pointermove', e => {
    const cur = $('wb-erase-cursor');
    if (cur) {
      if (tool === 'eraser') {
        const r = stage.getBoundingClientRect();
        const d = eraserR * 2 * view.s;
        cur.style.display = 'block';
        cur.style.width = d + 'px'; cur.style.height = d + 'px';
        cur.style.left = (e.clientX - r.left - d / 2) + 'px';
        cur.style.top = (e.clientY - r.top - d / 2) + 'px';
      } else cur.style.display = 'none';
    }
    if (panning) {
      view.x -= (e.clientX - panning.px) / view.s;
      view.y -= (e.clientY - panning.py) / view.s;
      panning = { px: e.clientX, py: e.clientY };
      draw();
      return;
    }
    if (!drawing) return;
    /* Browsers batch pointer moves between frames and hand the skipped ones
       back through getCoalescedEvents. Using them roughly triples the sample
       rate on a fast stroke, which is what keeps a quick line from becoming a
       series of chords. */
    const evs = (e.getCoalescedEvents && e.getCoalescedEvents()) || [e];
    (evs.length ? evs : [e]).forEach(ev => {
      const [wx2, wy2] = toWorld(ev);
      if (drawing.erase) { eraseAt(wx2, wy2); return; }
      const pr2 = (ev.pointerType === 'pen' && ev.pressure > 0) ? ev.pressure : 0.5;
      drawing.points.push([wx2, wy2, +pr2.toFixed(2)]);
    });
    draw();
  });

  const endPointer = () => {
    panning = null;
    if (!drawing) return;
    if (!drawing.erase) {
      if (drawing.points.length < 2) wb.strokes.pop();
      markDirty();
    }
    drawing = null;
  };
  canvas.addEventListener('pointerup', endPointer);
  canvas.addEventListener('pointercancel', endPointer);
  canvas.addEventListener('pointerleave', () => {
    const cur = $('wb-erase-cursor'); if (cur) cur.style.display = 'none';
  });

  /* Wheel pans; ctrl/⌘ + wheel zooms about the cursor, which is what every
     canvas app does and what a trackpad pinch sends. */
  stage.addEventListener('wheel', e => {
    if (!wb) return;
    e.preventDefault();
    if (e.ctrlKey || e.metaKey) {
      const r = stage.getBoundingClientRect();
      const mx = view.x + (e.clientX - r.left) / view.s;
      const my = view.y + (e.clientY - r.top) / view.s;
      // Exponential, so each notch is a constant proportion — and a wider
      // range, because zooming out to survey a big board was capped too close.
      const next = Math.min(6, Math.max(0.06, view.s * Math.exp(-e.deltaY * 0.0042)));
      // Keep the point under the cursor fixed while the scale changes.
      view.x = mx - (e.clientX - r.left) / next;
      view.y = my - (e.clientY - r.top) / next;
      view.s = next;
    } else {
      view.x += e.deltaX / view.s;
      view.y += e.deltaY / view.s;
    }
    draw();
  }, { passive: false });

  // ---- images ----
  async function addImageFile(file) {
    if (!file || !wb) return;
    $('wb-saved').textContent = 'Uploading…';
    try {
      const safe = (file.name || 'image').replace(/[^\w.\- ]+/g, '_').slice(-100);
      const path = `teams/${activeTeamId}/notes/${wb.note.id}/wb_${Date.now()}_${safe}`;
      const { url } = await otterUpload(file, path, file.type || 'image/png');
      const bmp = await createImageBitmap(file).catch(() => null);
      // Land it in the middle of what you are looking at, at a sane size.
      const r = stage.getBoundingClientRect();
      const w = Math.min(420, bmp ? bmp.width : 320);
      const h = bmp ? w * (bmp.height / bmp.width) : 240;
      wb.images.push({
        url, path,
        x: view.x + (r.width / view.s - w) / 2,
        y: view.y + (r.height / view.s - h) / 2,
        w, h
      });
      renderOverlays();
      markDirty();
    } catch (e) {
      console.warn('whiteboard image failed:', e && e.message, e);
      alert((e && e.message) || 'Could not add that image.');
      $('wb-saved').textContent = '';
    }
  }
  $('wb-image-btn').addEventListener('click', () => $('wb-file').click());
  $('wb-file').addEventListener('change', e => {
    const f = e.target.files && e.target.files[0];
    e.target.value = '';
    addImageFile(f);
  });
  // Paste. Only while a board is open and the caret is not in a text box.
  document.addEventListener('paste', e => {
    if (!wb) return;
    const ae = document.activeElement;
    if (ae && ae.classList && ae.classList.contains('nb-txt')) return;
    const item = [...(e.clipboardData ? e.clipboardData.items : [])]
      .find(i => i.type && i.type.startsWith('image/'));
    if (!item) return;
    e.preventDefault();
    addImageFile(item.getAsFile());
  });

  /* ---- Persistence -------------------------------------------------------
     Strokes are chunked across documents in the same `pages` subcollection the
     notebook uses. A busy board would pass Firestore's 1 MB document limit as
     one blob; 300 strokes per document keeps each one comfortably small and
     means a chunk that has not changed is not rewritten. */
  const CHUNK = 300;
  function col() {
    return fbDb.collection('teams').doc(activeTeamId)
      .collection('notes').doc(wb.note.id).collection('pages');
  }

  async function save() {
    if (!wb || !wb.dirty) return;
    wb.dirty = false;
    try {
      const chunks = [];
      for (let i = 0; i < Math.max(1, Math.ceil(wb.strokes.length / CHUNK)); i++) {
        chunks.push(wb.strokes.slice(i * CHUNK, (i + 1) * CHUNK));
      }
      await Promise.all(chunks.map((c, i) => col().doc('wb' + i).set({
        index: i, isWhiteboard: true,
        strokesJson: JSON.stringify(c),
        textsJson: i === 0 ? JSON.stringify(wb.texts || []) : '[]',
        imagesJson: i === 0 ? JSON.stringify(wb.images || []) : '[]',
        updatedAt: new Date().toISOString()
      }, { merge: true })));
      // Chunks that no longer exist have to go, or deleted strokes come back.
      for (let i = chunks.length; i < (wb.chunkCount || 0); i++) {
        await col().doc('wb' + i).delete().catch(() => {});
      }
      wb.chunkCount = chunks.length;
      $('wb-saved').textContent = 'Saved';
    } catch (e) {
      console.warn('whiteboard save failed:', e && (e.code || e.message));
      $('wb-saved').textContent = 'Not saved';
      wb.dirty = true;
    }
  }

  window.wbOpen = async function wbOpen(note) {
    document.body.classList.add('wb-full');
    wrap.style.display = 'flex';
    if (wb && wb.note.id === note.id) { draw(); return; }
    wb = { note, strokes: [], texts: [], images: [], dirty: false, chunkCount: 0 };
    view = { x: -400, y: -300, s: 1 };
    $('wb-title').value = note.title || '';
    $('wb-saved').textContent = '';
    try {
      const snap = await col().orderBy('index').get();
      snap.docs.forEach(d => {
        const v = d.data();
        try { wb.strokes = wb.strokes.concat(JSON.parse(v.strokesJson || '[]')); } catch (e) {}
        if (v.textsJson) { try { wb.texts = JSON.parse(v.textsJson) || []; } catch (e) {} }
        if (v.imagesJson) { try { wb.images = JSON.parse(v.imagesJson) || []; } catch (e) {} }
      });
      wb.chunkCount = snap.size;
    } catch (e) {
      console.warn('whiteboard load failed:', e && (e.code || e.message));
    }
    renderOverlays();
    draw();
  };

  window.wbClose = function wbClose() {
    document.body.classList.remove('wb-full');
    wrap.style.display = 'none';
    if (saveTimer) { clearTimeout(saveTimer); saveTimer = null; }
    if (wb && wb.dirty) save();
    wb = null;
    closeBar();
  };

  // ---- toolbar ----
  const setTool = t => {
    tool = t;
    document.querySelectorAll('.wb-tool').forEach(b => b.classList.toggle('active', b.dataset.tool === t));
  };
  const POPS = ['wb-pen-pop', 'wb-color-pop', 'wb-eraser-pop'];
  function togglePop(id) {
    POPS.forEach(x => { const el = $(x); if (el) el.hidden = (x !== id) ? true : !el.hidden; });
  }
  document.addEventListener('click', e => {
    if (e.target.closest && (e.target.closest('.nb-pop') || e.target.closest('.nb-pop-wrap'))) return;
    POPS.forEach(x => { const el = $(x); if (el) el.hidden = true; });
  });
  document.querySelectorAll('.wb-tool').forEach(b => b.addEventListener('click', () => {
    setTool(b.dataset.tool);
    if (b.dataset.tool === 'pen') togglePop('wb-pen-pop');
    if (b.dataset.tool === 'eraser') togglePop('wb-eraser-pop');
  }));
  document.querySelectorAll('#wb-tooltype button').forEach(b => b.addEventListener('click', () => {
    toolType = b.dataset.t; setTool('pen');
    document.querySelectorAll('#wb-tooltype button').forEach(x => x.classList.toggle('on', x === b));
  }));
  document.querySelectorAll('#wb-width button').forEach(b => b.addEventListener('click', () => {
    width = parseFloat(b.dataset.w);
    document.querySelectorAll('#wb-width button').forEach(x => x.classList.toggle('on', x === b));
  }));
  document.querySelectorAll('#wb-erasetype button').forEach(b => b.addEventListener('click', () => {
    eraserMode = b.dataset.e;
    document.querySelectorAll('#wb-erasetype button').forEach(x => x.classList.toggle('on', x === b));
  }));
  document.querySelectorAll('#wb-erasesize button').forEach(b => b.addEventListener('click', () => {
    eraserR = parseFloat(b.dataset.r);
    document.querySelectorAll('#wb-erasesize button').forEach(x => x.classList.toggle('on', x === b));
  }));
  function pick(hex) {
    color = hex; setTool('pen');
    document.querySelectorAll('.nb-swatch[data-wbcolor]').forEach(x =>
      x.classList.toggle('on', x.dataset.wbcolor === hex));
  }
  document.querySelectorAll('.nb-swatch[data-wbcolor]').forEach(b =>
    b.addEventListener('click', () => pick(b.dataset.wbcolor)));
  const ch = $('wb-colors');
  if (ch) {
    ch.innerHTML = PALETTE.map(c => `<button type="button" class="nb-swatch" data-p="${c}" style="background:${c}"></button>`).join('');
    ch.querySelectorAll('[data-p]').forEach(b => b.addEventListener('click', () => { pick(b.dataset.p); togglePop(null); }));
  }
  $('wb-color-btn').addEventListener('click', e => { e.stopPropagation(); togglePop('wb-color-pop'); });

  $('wb-undo').addEventListener('click', () => {
    if (!wb || !wb.strokes.length) return;
    redoStack.push(wb.strokes.pop()); draw(); markDirty();
  });
  $('wb-redo').addEventListener('click', () => {
    if (!wb || !redoStack.length) return;
    wb.strokes.push(redoStack.pop()); draw(); markDirty();
  });
  $('wb-home').addEventListener('click', () => { view = { x: -400, y: -300, s: 1 }; draw(); });

  $('wb-back').addEventListener('click', () => {
    wbClose();
    if (typeof notesCloseEditor === 'function') notesCloseEditor();
  });
  $('wb-title').addEventListener('input', () => {
    if (typeof notesPatchActive === 'function') notesPatchActive({ title: $('wb-title').value });
  });

  const withText = fn => { if (editing) { fn(editing.t, editing.el); markDirty(); } };
  $('wb-tb-size').addEventListener('change', () => {
    const t = editing && editing.el;
    withText(tt => { tt.size = +$('wb-tb-size').value; });
    positionOverlays();
    if (t) t.focus();
  });
  $('wb-tb-bold').addEventListener('mousedown', e => { e.preventDefault();
    withText((t, el) => { t.bold = !t.bold; el.style.fontWeight = t.bold ? '700' : '400'; }); });
  $('wb-tb-italic').addEventListener('mousedown', e => { e.preventDefault();
    withText((t, el) => { t.italic = !t.italic; el.style.fontStyle = t.italic ? 'italic' : 'normal'; }); });
  const inkHost = $('wb-tb-colors');
  if (inkHost) {
    inkHost.innerHTML = ['#111827', '#ffffff', '#dc2626', '#2563eb', '#16a34a', '#ca8a04']
      .map(c => `<button type="button" data-ink="${c}" style="background:${c}"></button>`).join('');
    inkHost.querySelectorAll('[data-ink]').forEach(b => b.addEventListener('mousedown', e => {
      e.preventDefault();
      withText((t, el) => { t.color = b.dataset.ink; el.style.color = t.color; });
    }));
  }
  $('wb-tb-del').addEventListener('mousedown', e => {
    e.preventDefault();
    if (!editing) return;
    wb.texts.splice(+editing.box.dataset.ti, 1);
    closeBar();
    renderOverlays();
    markDirty();
  });

  let rz = null;
  window.addEventListener('resize', () => { clearTimeout(rz); rz = setTimeout(draw, 120); });
  window.addEventListener('beforeunload', () => { if (wb && wb.dirty) save(); });
}

onReady(initWhiteboard);
